import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useHttp from "hooks/use-http";
import { useForm } from "react-hook-form";
import { Button, Grid, TextField } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import TableCM from "components/shared/Table";
import GenerateHeadCell from "helper/generate-head-cell";
import { yupResolver } from "@hookform/resolvers/yup";
import { addAnswerSchema } from "validations";
import {
  getQuestionAnswerByIdRequest,
  deleteQuestionAnswerRequest,
  confirmQuestionAnswerRequest,
  getAnswersRequest,
  addAnswerRequest,
} from "api/requests";

import { Check, RemoveCircleOutline } from "@mui/icons-material";

import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import BackButton from "components/shared/BackButton";
import DeleteModal from "components/shared/Modal/Delete";
import RejectModal from "components/shared/Modal/Reject";
import AcceptModal from "components/shared/Modal/Accept";
import Toast from "helper/toast";

import {
  StyledStack,
  StyledTitle,
  CustomTextField,
  ButtonContainer,
} from "./MessageDetails.style";

const headCells = GenerateHeadCell("Answers");

export default function MessageDetails() {
  const navigate = useNavigate();
  const params = useParams();
  const { request, loading, setLoading } = useHttp();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageInfo, setMessageInfo] = useState({ data: [], count: 0 });
  const [answerInfo, setAnswersInfo] = useState({ data: [], count: 0 });
  const [jDate, setJDate] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [answerId, setAnswerId] = useState("");
  const [answer, setAnswer] = useState("");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(addAnswerSchema),
  });
  const handelShowModal = () => setShowDeleteModal((state) => !state);

  const onSubmitAnswer = async (data) => {
    const res = await request({
      url: () =>
        addAnswerRequest({
          ...data,
          question_id: params.messageId,
        }),
    });

    if (res) {
      getMessageInfo();
      getAnswersInfo();
      reset({
        answer: "",
      });
      Toast({ type: "success", title: "ثبت پاسخ" });
    }
  };

  const getMessageInfo = useCallback(async () => {
    setLoading(true);
    const res = await request({
      url: getQuestionAnswerByIdRequest,
      values: params.messageId,
    });
    setLoading(false);

    if (res) {
      if (res.count < 1) {
        return setMessageInfo({
          count: res.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }
      setMessageInfo(res);
      setJDate(res.created_at.jdate.split(" ")[0]);
    }
  }, [request, params.complaintId]);

  const getAnswersInfo = useCallback(async () => {
    setLoading(true);
    const res = await request({
      url: getAnswersRequest,
      values: { question_id: params.messageId },
    });
    setLoading(false);

    if (res) {
      let transformedData = res.data.map((items) => ({
        id: items.id,
        full_name: items.text,
        is_confirmed:
          items?.is_confirmed == true ? "تایید شده" : "در انتظار تایید",
        confirmation: (
          <Fragment>
            {items?.is_confirmed == true ? (
              <Button
                color="success"
                startIcon={<Check />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowAcceptModal(items.id);
                }}
                sx={{ mr: 1 }}
                disabled
              >
                تایید
              </Button>
            ) : (
              <Button
                color="success"
                startIcon={<Check />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowAcceptModal(items.id);
                }}
                sx={{ mr: 1 }}
              >
                تایید
              </Button>
            )}
            <Button
              color="error"
              startIcon={<RemoveCircleOutline />}
              onClick={(e) => {
                e.stopPropagation();
                handleShowRejectModal(items.id);
              }}
            >
              حذف
            </Button>
          </Fragment>
        ),
      }));

      setAnswersInfo({
        data: transformedData,
        count: res.count,
      });
    }
  }, [request, params.messageId]);

  const handleShowRejectModal = (rowId) => {
    setShowRejectModal((state) => !state);
    setAnswerId(rowId);
  };

  const onReject = async () => {
    const res = await request({
      url: deleteQuestionAnswerRequest,
      values: answerId,
    });
    if (res) {
      getMessageInfo();
      getAnswersInfo();
      setShowRejectModal(false);
      Toast({ type: "success", title: "حذف پرسش" });
    }
  };

  const handleShowAcceptModal = (rowId) => {
    setShowAcceptModal((state) => !state);
    setAnswerId(rowId);
  };

  const onAcceptAnswer = async () => {
    const res = await request({
      url: confirmQuestionAnswerRequest,
      values: answerId,
    });
    if (res) {
      getMessageInfo();
      getAnswersInfo();
      setShowAcceptModal(false);
      Toast({ type: "success", title: "تایید پرسش " });
    }
  };

  useEffect(() => {
    getMessageInfo();
    getAnswersInfo();
  }, [getMessageInfo, getAnswersInfo]);

  const onDelete = async () => {
    const res = await request({
      url: deleteQuestionAnswerRequest,
      values: params.messageId,
    });

    if (res) {
      Toast({ type: "success", title: "حذف سوال" });
      navigate(-1);
    }
  };

  const onAccept = async () => {
    const res = await request({
      url: confirmQuestionAnswerRequest,
      values: params.messageId,
    });

    if (res) {
      Toast({ type: "success", title: "تایید سوال" });
    }
  };

  return (
    <DrawerCM title="نمایش پرسش">
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <Grid container my={2}>
            <Grid item mt={3} xs={12}>
              <BackButton />
            </Grid>
            <StyledStack>
              <StyledTitle>نمایش جزئیات پرسش</StyledTitle>
              <Grid container>
                <Grid item md={6} xs={12} p={1.5}>
                  <CustomTextField>
                    <span
                      style={{
                        color:
                          messageInfo?.is_confirmed == true ? "green" : "gray",
                      }}
                    >
                      نام و نام خانوادگی :
                    </span>
                    <div style={{ padding: "15px 0px 0px 0px" }}>
                      {messageInfo?.author?.full_name}
                    </div>
                  </CustomTextField>
                </Grid>
                <Grid item md={6} xs={12} p={1.5}>
                  <CustomTextField>
                    <span
                      style={{
                        color:
                          messageInfo?.is_confirmed == true ? "green" : "gray",
                      }}
                    >
                      {" "}
                      شماره تماس :{" "}
                    </span>
                    <div style={{ padding: "15px 0px 0px 0px" }}>
                      {messageInfo?.author?.phone_number}
                    </div>
                  </CustomTextField>
                </Grid>
                <Grid item md={6} xs={12} p={1.5}>
                  <CustomTextField>
                    <span
                      style={{
                        color:
                          messageInfo?.is_confirmed == true ? "green" : "gray",
                      }}
                    >
                      تاریخ ارسال :{" "}
                    </span>
                    <div style={{ padding: "15px 0px 0px 0px" }}>{jDate}</div>
                  </CustomTextField>
                </Grid>
                <Grid item md={6} xs={12} p={1.5}>
                  <CustomTextField>
                    <span
                      style={{
                        color:
                          messageInfo?.is_confirmed == true ? "green" : "gray",
                      }}
                    >
                      نام محصول :{" "}
                    </span>
                    <div style={{ padding: "15px 0px 0px 0px" }}>
                      {messageInfo?.product?.title}
                    </div>
                  </CustomTextField>
                </Grid>
                <Grid item xs={12} p={1.5}>
                  <CustomTextField>
                    <span
                      style={{
                        color:
                          messageInfo?.is_confirmed == true ? "green" : "gray",
                      }}
                    >
                      متن پرسش :{" "}
                    </span>
                    <div style={{ padding: "15px 0px 0px 0px" }}>
                      {messageInfo.text}
                    </div>
                  </CustomTextField>
                </Grid>
              </Grid>
              <ButtonContainer>
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  variant="contained"
                  sx={{ mt: 1, mr: 1 }}
                  onClick={handelShowModal}
                >
                  حذف
                </Button>
                {messageInfo?.is_confirmed == true ? (
                  ""
                ) : (
                  <Button
                    color="success"
                    startIcon={<CheckIcon />}
                    variant="contained"
                    sx={{ mt: 1, mr: 1 }}
                    onClick={onAccept}
                  >
                    تایید
                  </Button>
                )}
              </ButtonContainer>
            </StyledStack>
          </Grid>
          <Grid container spacing={1}>
            <StyledStack>
              <StyledTitle>پاسخ جدید</StyledTitle>
              <Grid container>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <TextField
                    id="answer"
                    label="پاسخ"
                    fullWidth
                    multiline
                    rows={4}
                    {...register("answer")}
                    error={Boolean(errors["answer"])}
                    helperText={errors["answer"]?.message}
                    style={{ width: "100%", overflow: "visible" }}
                    onChange={(e) => setAnswer(e.target.value)}
                  />
                </Grid>
                <ButtonContainer>
                  <Button
                    variant="contained"
                    onClick={handleSubmit(onSubmitAnswer)}
                    startIcon={<CheckIcon />}
                    sx={{ mt: 1 }}
                    disabled={answer.length <= 3 ? true : false}
                  >
                    ثبت پاسخ
                  </Button>
                </ButtonContainer>
              </Grid>
            </StyledStack>
          </Grid>

          <Grid item xs={12} my={2}>
            <TableCM
              title="لیست پاسخ ها"
              rows={answerInfo.data}
              headCells={headCells}
              page={1}
            />
          </Grid>

          <DeleteModal
            show={showDeleteModal}
            onClose={handelShowModal}
            onDelete={onDelete}
          />
          <RejectModal
            show={showRejectModal}
            onClose={handleShowRejectModal}
            onReject={onReject}
          />
          <AcceptModal
            show={showAcceptModal}
            onClose={handleShowAcceptModal}
            onAccept={onAcceptAnswer}
          />
        </Fragment>
      )}
    </DrawerCM>
  );
}
